/**
 * #703 — which Clients' Map pins may carry vitals, and what those vitals are.
 *
 * Background: the map's pins come from `clientPinMapper`, which maps
 * coordinates only, so no live pin has ever carried `vitals`. compass-map#8
 * stopped the card rendering a row of "—" placeholders for them. This module
 * supplies the other half: a client whose Apple Watch is enrolled AND online
 * AND reporting DOES have readings, and only that client's pin gets them.
 *
 * The enrollment flag needs no per-client request. `GET /api/v1/clients`
 * serializes with a strip-list (cortex-be `serializeClientForRoster`), so every
 * non-credential User column — `appleWatchEnabled` included — is already on the
 * roster this panel fetches. Only the READINGS are fetched, and only for the
 * enrolled set — then gated again on a live `DEVICE_KIND.WATCH` presence row
 * (#703 AC: enrolled-but-offline shows no row).
 *
 * Freshness uses the same live boundary as `WatchVitalsSection`, so the map
 * card and the profile pane cannot disagree about what "live" means.
 */
import { metricFreshnessTier } from "../clientProfile/WatchVitalsSection";
import {
  DEVICE_KIND,
  parseClientDevices,
} from "../../utils/devicePresenceUtils";

/** Metric types the map card renders, in its own field vocabulary. */
const MAP_VITAL_BY_METRIC_TYPE = Object.freeze({
  heart_rate: "hr",
  oxygen_saturation: "spo2",
  blood_oxygen: "spo2",
  spo2: "spo2",
  respiratory_rate: "rr",
  body_temperature: "temp",
});

const LIVE_CLIENT_PIN_PREFIX = "live-client-";

/**
 * Roster rows whose Apple Watch integration is ON.
 * Strictly `=== true`: a truthy string or 1 from a stale or hand-edited row
 * must not open a vitals fetch (mirrors `canEditClientDetails` / GMA-1864).
 * @param {unknown} roster response of GET /api/v1/clients
 * @returns {Array<number|string>} client ids, empty on any non-conforming input
 */
export function watchEnabledClientIds(roster) {
  if (!Array.isArray(roster)) return [];
  return roster
    .filter(
      (row) =>
        row &&
        typeof row === "object" &&
        row.appleWatchEnabled === true &&
        row.id !== null &&
        row.id !== undefined,
    )
    .map((row) => row.id);
}

/**
 * True when the Firebase devices map carries an online Apple Watch.
 * Absence / parse failure → false (fail closed: no vitals row).
 * @param {unknown} devicesMap `status/<id>/devices` value
 * @param {number} [now]
 */
export function hasOnlineWatchDevice(devicesMap, now = Date.now()) {
  return parseClientDevices(devicesMap, { onlineOnly: true, nowMs: now }).some(
    (d) => d.kind === DEVICE_KIND.WATCH,
  );
}

/**
 * Normalize `fetchClientLatestHealthMetrics` payload to a rows array.
 * The service returns `{ metrics, days }` (see watchService contract test);
 * an already-unwrapped array is accepted for callers/tests.
 * @param {unknown} payload
 * @returns {Array|null}
 */
export function metricsRowsFromLatestPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object" && Array.isArray(payload.metrics)) {
    return payload.metrics;
  }
  return null;
}

/**
 * Map card displays temp as °F. Health rows may carry C or F — convert when
 * known, omit when the unit is missing/unknown (safer than mislabeling °C as °F).
 * @param {object} row
 * @param {number} value already finite and > 0
 * @returns {number|null}
 */
export function tempFahrenheitFromMetricRow(row, value) {
  const raw = row && typeof row.unit === "string" ? row.unit.trim().toLowerCase() : "";
  const unit = raw.replace(/°/g, "");
  if (unit === "f" || unit === "fahrenheit") return value;
  if (unit === "c" || unit === "celsius") {
    return Math.round(((value * 9) / 5 + 32) * 10) / 10;
  }
  return null;
}

/**
 * Latest-per-type rows → the map card's `{hr, spo2, rr, temp}`, keeping only
 * readings inside the pane's live boundary.
 * @param {unknown} rowsOrPayload array OR `{ metrics }` from the latest endpoint
 * @param {number} [now]
 * @returns {{hr?:number, spo2?:number, rr?:number, temp?:number}|null} null when
 *   nothing survives — the card then renders no vitals row at all.
 */
export function vitalsFromLatestMetrics(rowsOrPayload, now = Date.now()) {
  const rows = metricsRowsFromLatestPayload(rowsOrPayload);
  if (!Array.isArray(rows)) return null;
  // Several raw metricTypes collapse onto one map field (e.g. spo2 aliases).
  // Keep the newest recordedAt per mapped field so iteration order cannot let
  // an older alias overwrite a newer one (Copilot on #706).
  const bestByField = {};
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const field = MAP_VITAL_BY_METRIC_TYPE[row.metricType];
    if (!field) continue;
    if (metricFreshnessTier(row.recordedAt, now) !== "live") continue;
    const value = Number(row.value);
    // Reject null/""/NaN and non-positive readings: the card's own formatter
    // shows only finite positives, so anything else would render as "—" and
    // put the placeholder row back.
    if (row.value === null || row.value === undefined || row.value === "") continue;
    if (!Number.isFinite(value) || value <= 0) continue;
    let mappedValue = value;
    if (field === "temp") {
      const f = tempFahrenheitFromMetricRow(row, value);
      if (f === null) continue;
      mappedValue = f;
    }
    const recordedMs = Date.parse(row.recordedAt);
    const prev = bestByField[field];
    if (
      prev &&
      Number.isFinite(prev.recordedMs) &&
      Number.isFinite(recordedMs) &&
      recordedMs < prev.recordedMs
    ) {
      continue;
    }
    bestByField[field] = {
      value: mappedValue,
      recordedMs: Number.isFinite(recordedMs) ? recordedMs : 0,
    };
  }
  const vitals = {};
  Object.keys(bestByField).forEach((field) => {
    vitals[field] = bestByField[field].value;
  });
  return Object.keys(vitals).length > 0 ? vitals : null;
}

/**
 * Copy `pins`, attaching each client's vitals by id. Never mutates the input
 * and never attaches an empty object, so a pin the map already carries (a demo
 * fixture) keeps whatever it had.
 * @param {Array<object>} pins MapPage roster entries
 * @param {Record<string, object|null>} vitalsByClientId keyed by RAW client id
 * @returns {Array<object>} a new array; elements with attached vitals are new
 *   objects, others may be the same reference as the input pin
 */
export function attachVitalsToPins(pins, vitalsByClientId) {
  if (!Array.isArray(pins)) return [];
  if (!vitalsByClientId || typeof vitalsByClientId !== "object") return [...pins];
  return pins.map((pin) => {
    if (!pin || typeof pin !== "object") return pin;
    // Prefer explicit clientId (dual-pin roster, compass-map#26). Fall back to
    // stripping live-client- and an optional -home|-device suffix.
    let rawId = null;
    if (pin.clientId !== null && pin.clientId !== undefined) {
      rawId = String(pin.clientId);
    } else {
      const id = typeof pin.id === "string" ? pin.id : "";
      if (!id.startsWith(LIVE_CLIENT_PIN_PREFIX)) return pin;
      rawId = id.slice(LIVE_CLIENT_PIN_PREFIX.length).replace(/-(home|device)$/i, "");
    }
    const vitals = vitalsByClientId[rawId];
    if (!vitals || typeof vitals !== "object" || Object.keys(vitals).length === 0) {
      return pin;
    }
    return { ...pin, vitals };
  });
}

